import {selectCurrentPlayer, selectGameType, selectUnits} from '../redux/reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../constants/game';
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from '../configs/playbackSpeed';
import {ABILITY_RESULT_HOLD_MARGIN_MS} from '../configs/abilityCastAnimation';
import {getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {
  markAwaitedAnimationFinished,
  markAwaitedAnimationPlaying,
  markAwaitedAnimationResultShown,
} from '../utils/replay/replayAwaitedAnimations';
import {
  buildGoblinsBombRichCastPlan,
  buildGoblinsHiddenTrapRichCastPlan,
  buildGoblinsTrapRichCastPlan,
  buildLeprechaunsTheftRichCastPlan,
  buildSeductionRichCastPlan,
} from '../utils/effects/buildFactionAbilityCast';
import {
  buildUnitUpgradeCastPlan,
  resolveAbilityCastTimeScale,
  resolveGoblinsBombOutcome,
} from '../utils/effects/buildAbilityCast';
import {
  captureBuildingDamageHold,
  captureSeductionHold,
  captureTrapSetupHold,
  captureUnitUpgradeHold,
  holdAbilityResult,
  releaseAbilityResult,
} from '../utils/effects/abilityResultHolds';
import {getActingPlayerFromState} from '../utils/players/getActingPlayer';
import {resolveShotPaceMs} from './playShotAnimation';
import {playCoinsGainedAnimation, playCoinsSpentAnimation} from './playCoinsSpentAnimation';

/**
 * ЭФФЕКТЫ СПОСОБНОСТЕЙ ФРАКЦИЙ — анимация на поле, звук и момент, когда
 * открывается итог (см. configs/abilityCastAnimation). Зовется ПОСЛЕ того, как
 * экшен способности применен, — и для своего действия (клик по цели), и для
 * чужого (сеть, бот, запись матча: playEffectForServerAction).
 *
 * С АНИМАЦИЕЙ: сразу звучит заклинание (или бросок бомбы), итог способности на
 * поле придержан (utils/effects/abilityResultHolds), а в момент попадания он
 * открывается, звучит звук успеха или провала и зовется onImpact (тост).
 *
 * БЕЗ АНИМАЦИИ все это случается сразу, как раньше:
 *  - в скрытой вкладке — кадры там не идут;
 *  - в записи матча быстрее x5 — это перемотка;
 *  - когда чужое действие не укладывается в темп даже сжатым (боты быстрее
 *    x2, см. resolveAbilityCastTimeScale);
 *  - когда нет поля, отряда или ячейки.
 *
 * Анимация — украшение: любая ее поломка сводится к показу итога сразу.
 */

let nextHoldId = 1;

export const checkIfAnimationAllowed = (store, hexGridRenderer) => {
  if (!hexGridRenderer?.abilityCastAnimation || typeof hexGridRenderer.getAbilityCastAnchor !== 'function') {
    return false;
  }
  if (typeof document !== 'undefined' && document.hidden) {
    return false;
  }
  const isReplay = selectGameType(store.getState()) === GAME_TYPES.REPLAY;
  return !(isReplay && getReplayPlaybackSpeedMultiplier() > FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER);
};

/**
 * Темп чужого действия: через столько придет следующее. Запись матча темп не
 * задает — ее проигрыватель ждет конца анимации сам (см. ReplayRunner)
 */
const resolvePaceMs = (store, isPaced, actorOwnerId) => {
  const state = store.getState();
  if (!isPaced || selectGameType(state) === GAME_TYPES.REPLAY) {
    return undefined;
  }
  return resolveShotPaceMs(state, actorOwnerId);
};

/**
 * Общий ход эффекта способности
 *
 * @param {Object} params
 * @param {function(): Object|null} params.buildPlan — план анимации (null — показать нечего)
 * @param {Object|null} [params.hold] — удержание итога (см. capture*Hold)
 * @param {function(): void} [params.onCast] — звук заклинания (только с анимацией)
 * @param {function(): void} params.onResult — звук итога, тост
 * @returns {boolean} пошла ли анимация
 */
export const runAbilityCast = (store, hexGridRenderer, {isPaced, actorOwnerId, buildPlan, hold, onCast, onResult}) => {
  const showResultNow = () => {
    onResult();
    return false;
  };

  if (!checkIfAnimationAllowed(store, hexGridRenderer)) {
    return showResultNow();
  }

  const holdKey = `ability:${nextHoldId}`;
  nextHoldId += 1;
  // Сначала итог придерживается: экшен уже применен, и поле могло успеть
  // нарисовать итог. Перерисовка с удержанием — одна: опорные точки плана
  // берутся уже с нее
  if (hold) {
    holdAbilityResult(holdKey, hold);
    hexGridRenderer.requestFieldRedraw?.();
  }

  let plan;
  try {
    plan = buildPlan();
  } catch (error) {
    console.warn('[AbilityCast] не удалось построить анимацию', error);
    plan = null;
  }
  const timeScale = plan
    ? resolveAbilityCastTimeScale(plan.impactMs, resolvePaceMs(store, isPaced, actorOwnerId))
    : null;
  if (timeScale === null) {
    if (releaseAbilityResult(holdKey)) {
      hexGridRenderer.requestFieldRedraw?.();
    }
    return showResultNow();
  }

  const durationMs = plan.totalMs * timeScale;
  let isResultShown = false;
  const showResult = () => {
    if (isResultShown) {
      return;
    }
    isResultShown = true;
    if (releaseAbilityResult(holdKey)) {
      hexGridRenderer.requestFieldRedraw?.();
    }
    // Запись матча дает итогу побыть на поле (см. ReplayRunner)
    markAwaitedAnimationResultShown();
    onResult();
  };

  let fallbackTimeoutId = null;
  const animationId = hexGridRenderer.abilityCastAnimation.play(plan, {
    timeScale,
    onImpact: showResult,
    onComplete: () => {
      clearTimeout(fallbackTimeoutId);
      markAwaitedAnimationFinished(holdKey);
      showResult();
    },
  });
  if (animationId === null) {
    showResult();
    return false;
  }

  markAwaitedAnimationPlaying(holdKey, Date.now() + durationMs + ABILITY_RESULT_HOLD_MARGIN_MS);
  // Кадры встали (вкладку скрыли посреди анимации) — итог не ждет возврата
  fallbackTimeoutId = setTimeout(() => {
    hexGridRenderer.abilityCastAnimation.finish(animationId);
  }, durationMs + ABILITY_RESULT_HOLD_MARGIN_MS);

  onCast?.();
  return true;
};

const resolveUnitHexKey = (store, unitId, units) => (
  (units || selectUnits(store.getState()) || []).find((unit) => unit.id === unitId)?.hexKey
);

/**
 * Соблазн суккубов: сердце летит от суккуба к цели
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} soundManager
 * @param {Object} params
 * @param {string} params.unitId — суккуб
 * @param {string} params.targetUnitId — цель
 * @param {boolean} params.success
 * @param {Object} [params.prevState] — состояние до экшена (для удержания итога)
 * @param {boolean} [params.isPaced] — действие чужое (бот, соперник, запись)
 * @param {function(): void} [params.onImpact] — тост итога
 * @returns {boolean} пошла ли анимация
 */
export const playSuccubesSeductionEffects = (store, hexGridRenderer, soundManager, {
  unitId, targetUnitId, success, cost, prevState, isPaced = false, onImpact,
}) => {
  const units = selectUnits(store.getState()) || [];
  const attackerUnit = units.find((unit) => unit.id === unitId);
  const attackerHexKey = attackerUnit?.hexKey;
  const targetHexKey = resolveUnitHexKey(store, targetUnitId, units);

  // Цена попытки списана сразу, при любом исходе
  if (attackerHexKey && cost > 0) {
    playCoinsSpentAnimation(store, hexGridRenderer, {hexKey: attackerHexKey, amount: cost, isPaced});
  }

  return runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: attackerUnit?.ownerId,
    buildPlan: () => {
      const attacker = hexGridRenderer.getAbilityCastAnchor(attackerHexKey);
      const target = hexGridRenderer.getAbilityCastAnchor(targetHexKey);
      return attacker && target
        ? buildSeductionRichCastPlan({attacker, target, hexSize: attacker.hexSize, hexRotation: attacker.hexRotation, success})
        : null;
    },
    hold: prevState ? captureSeductionHold(prevState, {unitId, targetUnitId}) : null,
    onCast: () => soundManager?.playSuccubesSeductionCast?.(),
    onResult: () => {
      if (success) {
        soundManager?.playSuccubesSeductionSuccess?.();
      } else {
        soundManager?.playSuccubesSeductionFailure?.();
      }
      onImpact?.();
    },
  });
};

/**
 * Бомба гоблинов: летит от гоблина к строению
 *
 * @param {Object} params — как у playSuccubesSeductionEffects, плюс:
 * @param {string} params.hexKey — ячейка строения
 * @param {boolean} [params.destroyed] — строение разрушено
 * @param {number} [params.damageIncrement] — урон (0 — крепость выдержала)
 */
export const playGoblinsBuildingDamageEffects = (store, hexGridRenderer, soundManager, {
  unitId, hexKey, success, destroyed, damageIncrement, prevState, isPaced = false, onImpact,
}) => {
  const attackerUnit = (selectUnits(store.getState()) || []).find((unit) => unit.id === unitId);
  const outcome = resolveGoblinsBombOutcome({success, destroyed, damageIncrement});

  return runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: attackerUnit?.ownerId,
    buildPlan: () => {
      const attacker = hexGridRenderer.getAbilityCastAnchor(attackerUnit?.hexKey);
      const target = hexGridRenderer.getAbilityCastAnchor(hexKey);
      return attacker && target
        ? buildGoblinsBombRichCastPlan({attacker, target, hexSize: attacker.hexSize, hexRotation: attacker.hexRotation, outcome})
        : null;
    },
    // Провал строение не задевает, но и его исход нельзя выдать раньше времени:
    // плашка с броском и формула в футере ждут, пока бомба упадет
    hold: prevState ? captureBuildingDamageHold(prevState, {unitId, hexKey}) : null,
    onCast: () => soundManager?.playGoblinsBombThrow?.(),
    onResult: () => {
      if (success) {
        soundManager?.playGoblinsBuildingDamageSuccess?.({destroyed, unitType: attackerUnit?.type});
      } else {
        soundManager?.playGoblinsBuildingDamageFailure?.();
      }
      onImpact?.();
    },
  });
};

/**
 * Установка ловушки гоблинов. Ячейку ловушки видит только ее владелец (как и
 * саму ловушку): у остальных гоблин колдует на месте
 *
 * @param {Object} params — как у playSuccubesSeductionEffects, плюс:
 * @param {string} params.trapId — id новой ловушки
 * @param {string} params.hexKey — ячейка ловушки
 * @param {string} params.ownerId — владелец ловушки
 * @param {number} [params.cost] — цена попытки
 */
export const playGoblinsTrapSetupEffects = (store, hexGridRenderer, soundManager, {
  unitId, trapId, hexKey, ownerId, success, cost, prevState, isPaced = false, onImpact,
}) => {
  const state = store.getState();
  const attackerUnit = (selectUnits(state) || []).find((unit) => unit.id === unitId);
  const isTrapVisible = getActingPlayerFromState(state)?.id === ownerId;

  if (attackerUnit?.hexKey && cost > 0) {
    playCoinsSpentAnimation(store, hexGridRenderer, {hexKey: attackerUnit.hexKey, amount: cost, isPaced});
  }

  return runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: attackerUnit?.ownerId,
    buildPlan: () => {
      const attacker = hexGridRenderer.getAbilityCastAnchor(attackerUnit?.hexKey);
      if (!attacker) {
        return null;
      }
      if (!isTrapVisible) {
        return buildGoblinsHiddenTrapRichCastPlan({attacker, hexSize: attacker.hexSize, hexRotation: attacker.hexRotation, success});
      }
      const target = hexGridRenderer.getAbilityCastAnchor(hexKey, {isTrapOutline: true});
      return target
        ? buildGoblinsTrapRichCastPlan({attacker, target, hexSize: attacker.hexSize, hexRotation: attacker.hexRotation, success})
        : null;
    },
    // Чужая ловушка на поле не видна вовсе — придерживать нечего, кроме
    // записи о броске
    hold: prevState ? captureTrapSetupHold(prevState, {unitId, trapId}) : null,
    onCast: () => soundManager?.playGoblinsTrapCast?.(),
    onResult: () => {
      if (success) {
        soundManager?.playGoblinsTrapSetupSuccess?.({unitType: attackerUnit?.type});
      } else {
        soundManager?.playGoblinsTrapSetupFailure?.();
      }
      onImpact?.();
    },
  });
};

/**
 * Улучшение отряда: золотые шевроны наплывают снизу и закрывают отряд, и тогда на
 * поле меняется его иконка и всплывает потраченное. Звук идет всегда: и общий
 * волшебный, и клич нового типа с монетами (см. SoundManager.playUnitUpgrade)
 *
 * @param {Object} params
 * @param {string} params.unitId
 * @param {string} params.newType — тип, в который отряд улучшен
 * @param {number} params.price
 * @param {Object} [params.prevState] — состояние до экшена (для удержания прежней иконки)
 * @param {boolean} [params.isPaced] — действие чужое (бот, соперник, запись)
 * @returns {boolean} пошла ли анимация
 */
export const playUnitUpgradeEffects = (store, hexGridRenderer, soundManager, {
  unitId, newType, price, prevState, isPaced = false,
}) => {
  const state = store.getState();
  const unit = (selectUnits(state) || []).find((item) => item.id === unitId);
  const player = selectCurrentPlayer(state);
  soundManager?.playUnitUpgrade?.(newType, player?.id, player, price, unit);

  return runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: unit?.ownerId,
    buildPlan: () => {
      const anchor = hexGridRenderer.getAbilityCastAnchor(unit?.hexKey);
      return anchor ? buildUnitUpgradeCastPlan({unit: anchor, hexSize: anchor.hexSize}) : null;
    },
    hold: prevState ? captureUnitUpgradeHold(prevState, {unitId}) : null,
    onResult: () => {
      if (unit?.hexKey) {
        playCoinsSpentAnimation(store, hexGridRenderer, {hexKey: unit.hexKey, amount: price, isPaced});
      }
    },
  });
};

/**
 * Снятие своей ловушки: над гоблином всплывает награда «+N»
 */
export const playGoblinsTrapRemovalEffects = (store, hexGridRenderer, soundManager, {unitId, reward, isPaced = false}) => {
  soundManager?.playGoblinsTrapRemoval?.();
  const hexKey = resolveUnitHexKey(store, unitId);
  if (hexKey) {
    playCoinsGainedAnimation(store, hexGridRenderer, {hexKey, amount: reward, isPaced});
  }
};

/**
 * Кража монет лепреконами: над отрядом-вором всплывает «+N» украденных монет, а
 * вокруг него поднимается поток искр. Поток идет только вместе с числом:
 * когда числа нет (скрытая вкладка, перемотка, быстрые боты), нечего и
 * сопровождать
 */
export const playLeprechaunsTheftEffects = (store, hexGridRenderer, soundManager, {unitId, coinsStolen, isPaced = false}) => {
  const thief = (selectUnits(store.getState()) || []).find((unit) => unit.id === unitId);
  soundManager?.playLeprechaunsTheft?.({unitType: thief?.type});
  const hexKey = thief?.hexKey;
  if (!hexKey || !playCoinsGainedAnimation(store, hexGridRenderer, {hexKey, amount: coinsStolen, isPaced})) {
    return;
  }
  try {
    const unit = hexGridRenderer.getAbilityCastAnchor?.(hexKey);
    if (unit) {
      hexGridRenderer.abilityCastAnimation?.play(
        buildLeprechaunsTheftRichCastPlan({unit, hexSize: unit.hexSize, hexRotation: unit.hexRotation})
      );
    }
  } catch (error) {
    // Анимация — украшение: монеты уже у вора, и число над ним показано
    console.warn('[AbilityCast] не удалось показать поток искр кражи', error);
  }
};
